'use client'
import React, { useCallback, useEffect } from 'react'

import { useGetBookConfirmQuery } from '@/src/api/rtkQuery/bookConfirmApi'

import { HeaderContainer } from '@/src/components/common/Header'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'
import { useTranslations } from 'next-intl'
import { BookingStatus, BookingStatusVariant } from './bookingStatus'
import useClevertapForBookFailed from '@/src/hooks/analytics/clevertap/bookFailed'

interface BookingStatusWrapperProps {
    isMobile: boolean
    variant: BookingStatusVariant
}

const BookingStatusWrapper: React.FC<BookingStatusWrapperProps> = ({ isMobile, variant }) => {
    const t = useTranslations('confirmationFailure')
    const itineraryId = useGetItineraryId()
    const goToHome = useRedirectToHome()
    const { triggerBookFailedEvent } = useClevertapForBookFailed()

    const { data: confirmationData, isLoading } = useGetBookConfirmQuery(itineraryId)

    const tripId = confirmationData?.tripId

    const handleViewDetails = useCallback(() => {
        if (tripId) {
            window.open(`/accounts/trips/${tripId}`, '_blank')
        }
    }, [tripId])

    const titleKeyMap: Record<BookingStatusVariant, string> = {
        failed: 'bookFailureTitle',
        pending: 'bookPendingTitle',
        refund: 'bookRefundTitle'
    }
    const subtitleKeyMap: Record<BookingStatusVariant, string> = {
        failed: 'bookFailureSubTitle',
        pending: 'bookPendingSubTitle',
        refund: 'bookRefundSubTitle'
    }

    const titleKey = titleKeyMap[variant]
    const subtitleKey = subtitleKeyMap[variant]

    const subtitle =
        variant === 'failed' || variant === 'refund' ? t(subtitleKey, { tripId: tripId || '' }) : t(subtitleKey)

    const errorBoundaryIdMap: Record<BookingStatusVariant, string> = {
        failed: 'HeaderContainer-desktop-wrapper-bookingStatus-failed',
        pending: 'HeaderContainer-desktop-wrapper-bookingStatus-pending',
        refund: 'HeaderContainer-desktop-wrapper-bookingStatus-refund'
    }
    const errorBoundaryId = errorBoundaryIdMap[variant]

    useEffect(() => {
        triggerBookFailedEvent('Air_Failures', {
            action_name: 'booking_status',
            action_value: variant,
            titleKey,
            subtitleKey,
            tripId
        })
    }, [])

    return (
        <>
            {!isMobile && (
                <ErrorBoundaryWrapper identifier={errorBoundaryId}>
                    <HeaderContainer />
                </ErrorBoundaryWrapper>
            )}
            <BookingStatus
                variant={variant}
                title={t(titleKey)}
                subtitle={subtitle}
                primaryButtonText={t('buttonCta')}
                onPrimaryClick={(isReloadRequired: boolean) => {
                    goToHome({ isReloadRequired })
                }}
                isLoading={isLoading}
                secondaryButtonText={tripId ? t('viewDetails') : undefined}
                onSecondaryClick={tripId ? handleViewDetails : undefined}
            />
        </>
    )
}

// Export specific wrappers for backward compatibility
export const BookingFailureWrapper: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
    <BookingStatusWrapper isMobile={isMobile} variant="failed" />
)

export const BookingPendingWrapper: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
    <BookingStatusWrapper isMobile={isMobile} variant="pending" />
)

export const BookingRefundWrapper: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
    <BookingStatusWrapper isMobile={isMobile} variant="refund" />
)

export default BookingFailureWrapper
